// Phase 7 R3 — Receive Parts modal.
// Lets staff partially or fully receive PO lines in a single transaction.
//
// SAFETY: This dialog never writes to parts_lines.unit_cost or any pricing
// field. The actual cost the user enters is recorded on parts_receipt_lines
// and surfaced in audit (cost_corrected event) only — pricing is untouched.
import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useRecordPartsReceipt } from '@/hooks/purchasing/useRecordPartsReceipt';
import { toast } from '@/hooks/use-toast';
import type { PurchaseOrderLine } from '@/hooks/purchasing/usePurchaseOrder';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  shopId: string;
  purchaseOrderId: string;
  poNumber: string;
  lines: PurchaseOrderLine[];
}

interface RowState {
  qty: string;
  unitCost: string;
  notes: string;
}

const fmtMoney = (n: number | null | undefined) =>
  n == null ? '—' : Number(n).toLocaleString(undefined, { style: 'currency', currency: 'USD' });

export function ReceivePartsDialog({
  open,
  onOpenChange,
  shopId,
  purchaseOrderId,
  poNumber,
  lines,
}: Props) {
  const mut = useRecordPartsReceipt();
  const [packingSlip, setPackingSlip] = useState('');
  const [headerNotes, setHeaderNotes] = useState('');
  const [rows, setRows] = useState<Record<string, RowState>>({});

  // Only show lines that still have remaining qty AND are receivable.
  const eligible = useMemo(
    () =>
      lines
        .filter((l) => l.status === 'open' || l.status === 'partially_received')
        .map((l) => {
          const remaining = Math.max(
            0,
            Number(l.quantity_ordered) - Number(l.cumulative_received ?? 0),
          );
          return { line: l, remaining };
        })
        .filter((r) => r.remaining > 0),
    [lines],
  );

  const getRow = (id: string, defaultCost: number | null): RowState =>
    rows[id] ?? { qty: '', unitCost: defaultCost == null ? '' : String(defaultCost), notes: '' };

  const setRow = (id: string, patch: Partial<RowState>, defaultCost: number | null) => {
    setRows((prev) => ({
      ...prev,
      [id]: { ...getRow(id, defaultCost), ...patch },
    }));
  };

  // Validation: at least one positive qty, none over remaining.
  const { hasPositive, hasError } = useMemo(() => {
    let positive = false;
    let error = false;
    for (const { line, remaining } of eligible) {
      const r = rows[line.id];
      if (!r) continue;
      const q = Number(r.qty);
      if (!r.qty.trim()) continue;
      if (Number.isNaN(q) || q < 0) {
        error = true;
        continue;
      }
      if (q > 0) positive = true;
      if (q > remaining) error = true;
    }
    return { hasPositive: positive, hasError: error };
  }, [rows, eligible]);

  const handleSubmit = async () => {
    const payloadLines = eligible
      .map(({ line }) => {
        const r = rows[line.id];
        if (!r) return null;
        const q = Number(r.qty);
        if (!r.qty.trim() || Number.isNaN(q) || q <= 0) return null;
        const unitCost = r.unitCost.trim() === '' ? null : Number(r.unitCost);
        return {
          purchase_order_line_id: line.id,
          quantity_received: q,
          unit_cost_actual: unitCost == null || Number.isNaN(unitCost) ? null : unitCost,
          notes: r.notes.trim() ? r.notes.trim() : null,
        };
      })
      .filter(Boolean) as Array<{
        purchase_order_line_id: string;
        quantity_received: number;
        unit_cost_actual: number | null;
        notes: string | null;
      }>;

    if (payloadLines.length === 0) return;

    try {
      await mut.mutateAsync({
        shopId,
        purchaseOrderId,
        packing_slip_ref: packingSlip.trim() || null,
        notes: headerNotes.trim() || null,
        lines: payloadLines,
      });
      toast({
        title: 'Parts received',
        description: `${payloadLines.length} line${payloadLines.length === 1 ? '' : 's'} recorded on ${poNumber}.`,
      });
      // Reset and close
      setPackingSlip('');
      setHeaderNotes('');
      setRows({});
      onOpenChange(false);
    } catch (e: any) {
      toast({
        title: 'Receiving failed',
        description: e?.message ?? 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Receive parts — {poNumber}</DialogTitle>
          <DialogDescription>
            Enter the quantity received now for each line. Actual unit cost is recorded for
            audit but does not affect ticket pricing.
          </DialogDescription>
        </DialogHeader>

        {eligible.length === 0 ? (
          <div className="rounded-md border border-dashed bg-muted/20 p-6 text-center text-sm text-muted-foreground">
            All lines on this PO are fully received or not receivable.
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="md:col-span-1">
                <Label htmlFor="packing-slip" className="text-xs">Packing slip #</Label>
                <Input
                  id="packing-slip"
                  value={packingSlip}
                  onChange={(e) => setPackingSlip(e.target.value)}
                  placeholder="Optional"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="header-notes" className="text-xs">Notes</Label>
                <Input
                  id="header-notes"
                  value={headerNotes}
                  onChange={(e) => setHeaderNotes(e.target.value)}
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="space-y-2">
              {eligible.map(({ line, remaining }) => {
                const r = getRow(line.id, line.unit_cost_ordered);
                const q = Number(r.qty);
                const overReceive = r.qty.trim() !== '' && !Number.isNaN(q) && q > remaining;
                return (
                  <div
                    key={line.id}
                    className="rounded-md border bg-card p-3 space-y-2"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{line.description}</div>
                        <div className="text-xs text-muted-foreground">
                          {line.vendor_part_number && `${line.vendor_part_number} · `}
                          Ordered {Number(line.quantity_ordered)} ·
                          Already received {Number(line.cumulative_received)} ·
                          <span className="font-medium"> Remaining {remaining}</span>
                          {line.unit_cost_ordered != null && (
                            <> · Ordered cost {fmtMoney(line.unit_cost_ordered)}</>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                      <div>
                        <Label className="text-xs">Receive now</Label>
                        <Input
                          type="number"
                          min={0}
                          max={remaining}
                          step="any"
                          value={r.qty}
                          onChange={(e) =>
                            setRow(line.id, { qty: e.target.value }, line.unit_cost_ordered)
                          }
                          placeholder="0"
                          className={overReceive ? 'border-destructive' : ''}
                        />
                        {overReceive && (
                          <div className="mt-1 text-xs text-destructive">
                            Cannot exceed remaining ({remaining})
                          </div>
                        )}
                      </div>
                      <div>
                        <Label className="text-xs">Actual unit cost</Label>
                        <Input
                          type="number"
                          step="any"
                          value={r.unitCost}
                          onChange={(e) =>
                            setRow(line.id, { unitCost: e.target.value }, line.unit_cost_ordered)
                          }
                          placeholder={
                            line.unit_cost_ordered != null
                              ? String(line.unit_cost_ordered)
                              : 'Optional'
                          }
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Notes</Label>
                        <Input
                          value={r.notes}
                          onChange={(e) =>
                            setRow(line.id, { notes: e.target.value }, line.unit_cost_ordered)
                          }
                          placeholder="Optional"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={mut.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!hasPositive || hasError || mut.isPending || eligible.length === 0}
          >
            {mut.isPending ? 'Recording…' : 'Record receipt'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
